import React, { useState, useEffect, useRef } from 'react';
import { Languages, X, Check } from 'lucide-react';

const TRANSLATE_TEXTS = ['Translate', 'Traduire', 'Übersetzen', 'Traducir', '翻译', '翻訳', '번역', 'Перевести', 'ترجمة'];

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'Français' },
  { code: 'es', name: 'Español' },
  { code: 'de', name: 'Deutsch' },
  { code: 'it', name: 'Italiano' },
  { code: 'pt', name: 'Português' },
  { code: 'nl', name: 'Nederlands' },
  { code: 'ru', name: 'Русский' },
  { code: 'zh-CN', name: '中文 (简体)' },
  { code: 'ja', name: '日本語' },
  { code: 'ko', name: '한국어' },
  { code: 'ar', name: 'العربية' },
  { code: 'hi', name: 'हिन्दी' },
  { code: 'tr', name: 'Türkçe' },
  { code: 'pl', name: 'Polski' },
  { code: 'vi', name: 'Tiếng Việt' },
  { code: 'th', name: 'ไทย' },
  { code: 'sv', name: 'Svenska' },
  { code: 'da', name: 'Dansk' },
  { code: 'fi', name: 'Suomi' },
  { code: 'el', name: 'Ελληνικά' },
  { code: 'he', name: 'עברית' },
  { code: 'id', name: 'Bahasa Indonesia' },
  { code: 'ms', name: 'Bahasa Melayu' }
];

const LanguageSelector = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [textIndex, setTextIndex] = useState(0);
  const [currentLang, setCurrentLang] = useState('en');
  const popupRef = useRef(null);

  // Rotating text effect
  useEffect(() => {
    const interval = setInterval(() => {
      setTextIndex((prev) => (prev + 1) % TRANSLATE_TEXTS.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  // Initialize Google Translate script
  useEffect(() => {
    if (!document.getElementById('google-translate-script')) {
      const addScript = document.createElement('script');
      addScript.id = 'google-translate-script';
      addScript.setAttribute('src', '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit');
      document.body.appendChild(addScript);

      window.googleTranslateElementInit = () => {
        new window.google.translate.TranslateElement(
          {
            pageLanguage: 'en',
            autoDisplay: false,
          },
          'google_translate_element'
        );
      };
    }

    // Try to read current language from cookie
    const match = document.cookie.match(/googtrans=\/en\/([^;]+)/);
    if (match && match[1]) {
      setCurrentLang(match[1]);
    }

    // Click outside to close
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageSelect = (langCode) => {
    setCurrentLang(langCode);
    setIsOpen(false);
    
    // Set cookie and reload for reliable translation across all elements
    document.cookie = `googtrans=/en/${langCode}; path=/`;
    document.cookie = `googtrans=/en/${langCode}; domain=${window.location.hostname}; path=/`;
    window.location.reload();
  };

  return (
    <>
      {/* Hidden Google Translate Element */}
      <div id="google_translate_element" className="hidden"></div>

      <div className="fixed bottom-6 left-6 z-[100] group flex flex-col items-start gap-2" ref={popupRef}>
        
        {/* Custom Button */}
        <button 
          onClick={() => setIsOpen(!isOpen)}
          className="bg-white/95 backdrop-blur-md rounded-full shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-slate-200/60 p-2 pr-5 flex items-center gap-3 hover:shadow-[0_8px_30px_rgb(0,102,255,0.15)] transition-all transform hover:-translate-y-1 group"
        >
          <div className="bg-blue-50 text-blue-600 p-2.5 rounded-full flex-shrink-0 shadow-sm group-hover:bg-blue-600 group-hover:text-white transition-colors">
            <Languages className="w-5 h-5" />
          </div>
          <div className="overflow-hidden relative h-5 w-24 text-left">
            {TRANSLATE_TEXTS.map((text, idx) => (
              <span
                key={text}
                className={`absolute left-0 top-0 font-semibold text-sm text-slate-700 transition-all duration-500 w-full ${
                  idx === textIndex 
                    ? 'opacity-100 translate-y-0' 
                    : idx < textIndex || (textIndex === 0 && idx === TRANSLATE_TEXTS.length - 1)
                      ? 'opacity-0 -translate-y-full'
                      : 'opacity-0 translate-y-full'
                }`}
              >
                {text}
              </span>
            ))}
          </div>
        </button>
        


        {/* Custom Popup Modal */}
        {isOpen && (
          <div className="absolute bottom-full left-0 mb-4 bg-white rounded-2xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] border border-slate-100 w-[280px] sm:w-[320px] overflow-hidden flex flex-col transform origin-bottom-left animate-in slide-in-from-bottom-2 fade-in duration-200">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
              <h3 className="font-semibold text-slate-800">Select Language</h3>
              <button 
                onClick={() => setIsOpen(false)}
                className="p-1 rounded-full text-slate-400 hover:bg-slate-200 hover:text-slate-700 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            <div className="max-h-[350px] overflow-y-auto custom-scrollbar p-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleLanguageSelect(lang.code)}
                    className={`flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-medium transition-colors text-left ${
                      currentLang === lang.code 
                        ? 'bg-blue-50 text-blue-700' 
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                    }`}
                  >
                    <span className="truncate">{lang.name}</span>
                    {currentLang === lang.code && <Check className="w-4 h-4 flex-shrink-0" />}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default LanguageSelector;
